import React from "react";

import { Tab, Tabs } from "@material-ui/core";
import { ColDef, ColGroupDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";
import { v4 as uuidv4 } from "uuid";

import AgGrid from "components/AgGrid/AgGrid";
import CheckboxCellRenderer from "components/AgGrid/CheckboxCellRenderer";
import { CollapseTableWrapper } from "components/AgGrid/TableWrapper";
import { Button } from "components/Form";
import { Column, Row } from "components/Layout";
import { Paper } from "components/Paper";
import { SpeciesSelector } from "components/SpeciesSelector";

import { Settings } from "constants/settings";
import { colors } from "constants/theme";

import { getActiveRole, getAges, getSetting } from "selectors";

// The row shapes that flow through DeploymentAttributesTable (Ages, Breeds,
// Sexes, Dentition) share this small set of fields, but Dentition adds a few
// extras (age/display_value/default), so this is intentionally permissive
// rather than a single concrete entity shape. No index signature here (unlike
// the merged row this component builds internally) - that would block the
// real DeploymentAge/DeploymentBreed/etc types callers actually pass in from
// satisfying this type, since none of them have one either.
export interface DeploymentAttributeRow {
  id?: string;
  name?: string;
  order?: number;
  code?: string;
  is_used?: boolean;
  saleyard_code?: string;
  quick_code?: string;
  quick_select?: boolean;
  quick_select_hooks?: boolean;
  quick_select_paddock?: boolean;
  age?: string;
  age_id?: number;
  display_value?: string;
  default?: boolean;
  species_id?: number;
  deployment_id?: number;
  deploymentOrder?: number;
  touched?: boolean;
  syncing?: boolean;
  syncError?: boolean;
}

export interface DeploymentAttributesTableProps {
  baseData: DeploymentAttributeRow[];
  deploymentData: DeploymentAttributeRow[];
  dataKey: string;
  // Widened to `ColDef | ColGroupDef` (not `<DeploymentAttributeRow>`): each
  // caller's own concrete column defs (e.g. `ColDef<DeploymentBreed>[]`) aren't
  // assignable to a shared concrete `ColDef<DeploymentAttributeRow>[]` given
  // ag-grid's `ColDef<TData>` invariance - see the callers' own column defs.
  columnDefs: (ColDef | ColGroupDef)[];
  handleUpdate: (updates: Partial<DeploymentAttributeRow>[]) => void;
}

const SaveButton = styled(Button)`
  flex: 0;
  margin-left: auto;
  align-self: center;
`;

const touchRow = e => {
  if (!isEqual(e.newValue, e.oldValue)) {
    e.node.setData({
      ...e.node.data,
      touched: true,
    });
  }
};

const getRowStyle = row => {
  const { data } = row;
  if (data.syncing) {
    return {
      background: `${colors.primaryActive}50`,
    };
  }
  if (data.syncError) {
    return {
      background: colors.errorRed,
    };
  }
};

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  flex: 1,
  editable: true,
  enableCellChangeFlash: true,
};

const components = {
  checkboxRenderer: CheckboxCellRenderer,
};

const getRowId = params =>
  params.data.id ||
  get(params.data, params.context.dataKey) ||
  params.data.display_value ||
  params.data.name;

export const DeploymentAttributesTable = ({
  baseData,
  deploymentData,
  dataKey,
  columnDefs,
  handleUpdate,
}: DeploymentAttributesTableProps) => {
  const { deployments } = useSelector(getActiveRole);

  const selectedSpeciesId = useSelector(getSetting(Settings.speciesId));

  const [selectedDeployment, setSelectedDeployment] = React.useState(
    deployments[0].id,
  );

  const ages = useSelector(getAges);
  const agGridInstance = React.useRef<AgGridReact>();

  const onGridReady = params => {
    agGridInstance.current = params;
  };

  const handleSetSelectedDeployment = (_event, newValue) =>
    setSelectedDeployment(newValue);

  const save = () => {
    if (!agGridInstance.current) {
      return;
    }
    const updates = [];
    agGridInstance.current.api.forEachNode((rowNode, index) => {
      const { data } = rowNode;
      const reordered = data.deploymentOrder !== index;
      // Only update the row if its been touched or re-ordered.
      if (data.touched || reordered) {
        const payload = {
          order: index,
          [dataKey]: data[dataKey],
          deployment_id: selectedDeployment,
          code: data.code,
          is_used: data.is_used || false,
          id: data.id || uuidv4(),
          quick_select: data.quick_select,
          quick_select_hooks: data.quick_select_hooks,
          quick_select_paddock: data.quick_select_paddock,
          saleyard_code:
            dataKey === "breed_id" ? data.saleyard_code : undefined,
          quick_code: dataKey === "breed_id" ? data.quick_code : undefined,
        };
        updates.push(payload);
      }
    });

    if (updates.length > 0) {
      handleUpdate(updates);
    }
  };

  // combine

  const rowData = React.useMemo(() => {
    // Filter the base data by select species.
    const selectedSpeciesData = baseData.filter(
      attr => attr.species_id === selectedSpeciesId,
    );

    // Filter the deployment data by selected deployment
    const selectedDeploymentData = deploymentData.filter(
      attr => attr.deployment_id === selectedDeployment,
    );

    const data = selectedSpeciesData.map(attr => {
      const deploymentAttr = selectedDeploymentData.find(
        a => (a as Record<string, unknown>)[dataKey] === attr.id,
      );

      let isUsed = deploymentAttr?.is_used || false;
      // Dentitions should be used by default, so if no deploymentAttr, default to true
      if (dataKey === "dentition_id" && !deploymentAttr) {
        isUsed = true;
      }
      return {
        deploymentOrder: deploymentAttr?.order,
        id: deploymentAttr?.id || "",
        order: attr.order,
        code: deploymentAttr?.code || "",
        name: attr.name,
        [dataKey]: attr.id,
        is_used: isUsed,
        saleyard_code: deploymentAttr?.saleyard_code || "",
        quick_code: deploymentAttr?.quick_code || "",
        quick_select: deploymentAttr?.quick_select || false,
        quick_select_hooks: deploymentAttr?.quick_select_hooks || false,
        quick_select_paddock: deploymentAttr?.quick_select_paddock || false,
        age: dataKey === "dentition_id" ? ages[attr.age_id]?.name : undefined,
        display_value:
          dataKey === "dentition_id" ? attr.display_value : undefined,
        default: dataKey === "dentition_id" ? attr.default : undefined,
      };
    });
    return sortBy(data, ["deploymentOrder", "order"]);
  }, [
    baseData,
    dataKey,
    deploymentData,
    selectedDeployment,
    selectedSpeciesId,
    ages,
  ]);

  return (
    <Row flexGrow>
      {deployments.length > 1 && (
        <Column margin={2}>
          <Paper square elevation={2}>
            <Tabs
              orientation="vertical"
              value={selectedDeployment}
              onChange={handleSetSelectedDeployment}
            >
              {deployments.map(deployment => (
                <Tab
                  label={deployment.channel}
                  value={deployment.id}
                  key={deployment.id}
                />
              ))}
            </Tabs>
          </Paper>
        </Column>
      )}
      <Column full>
        <Row>
          <SpeciesSelector />

          <SaveButton onClick={save} data-tour="save">
            Save
          </SaveButton>
        </Row>
        <CollapseTableWrapper>
          <AgGrid
            context={{ dataKey }}
            onGridReady={onGridReady}
            onCellValueChanged={touchRow}
            rowData={rowData}
            undoRedoCellEditing
            undoRedoCellEditingLimit={200}
            suppressColumnVirtualisation
            suppressScrollOnNewData
            singleClickEdit
            columnDefs={columnDefs}
            rowDragManaged
            getRowStyle={getRowStyle}
            defaultColDef={defaultColDef}
            components={components}
            stopEditingWhenCellsLoseFocus
            getRowId={getRowId}
          />
        </CollapseTableWrapper>
      </Column>
    </Row>
  );
};

export default DeploymentAttributesTable;
